import Vue from 'vue'
import Vuex from 'vuex'
import { guestFieldNames, participants } from '@/utils/customer-guests'

import { createAuthStoreModule } from 'kayleen-vue-auth0-client'

import AttachmentService from './api/attachment-service'
import CustomerService from './api/customer-service'
import EvaluaionService from './api/evaluation-service'
import OrderService from './api/order-service'
import PriceService from './api/price-service'
import QuantityService from './api/quantity-service'
import ProductService from './api/product-service'
import SupplierService from './api/supplier-service'
import TextSnippetService from './api/text-snippet-service'
import CustomEmailTemplateService from './api/custom-email-template-service'
import EmailAssetService from './api/email-asset-service'
import EmailJobService from './api/email-job-service'
import ExternalDatabaseService from './api/external-database-service'
import CsvImportService from './api/csv-import-service'
import ProductImageService from './api/product-image-service'
import InfoDocumentService from './api/info-document-service'

import auth0Settings from './auth0-settings'
import settings from './settings'

const attachmentService = new AttachmentService(settings.apiUri)
const customerService = new CustomerService(settings.apiUri)
const evaluationService = new EvaluaionService(settings.apiUri)
const orderService = new OrderService(settings.apiUri)
const priceService = new PriceService(settings.apiUri)
const quantityService = new QuantityService(settings.apiUri)
const productService = new ProductService(settings.apiUri)
const supplierService = new SupplierService(settings.apiUri)
const textSnippetService = new TextSnippetService(settings.apiUri)
const customEmailTemplateService = new CustomEmailTemplateService(settings.apiUri)
const emailAssetService = new EmailAssetService(settings.apiUri)
const emailJobService = new EmailJobService(settings.apiUri)
const externalDatabaseService = new ExternalDatabaseService(settings.apiUri)
const csvImportService = new CsvImportService(settings.apiUri)
const productImageService = new ProductImageService(settings.apiUri)
const infoDocumentService = new InfoDocumentService(settings.apiUri)

Vue.use(Vuex)

export default new Vuex.Store({
  actions: {
    // evaluation
    async loadEvaluation({ commit, getters, rootGetters }) {
      const accessToken = rootGetters['auth/accessToken']
      const invitationCode = getters.currentCustomerInvitationCode

      const evaluation = await evaluationService.findForMe({ accessToken, invitationCode })

      commit('setEvaluation', { evaluation })
    },
    async createOrUpdateEvaluation({ commit, getters, rootGetters }, { evaluation }) {
      const accessToken = rootGetters['auth/accessToken']
      const invitationCode = getters.currentCustomerInvitationCode

      await evaluationService.createOrUpdateForMe(evaluation, { accessToken, invitationCode })
    },

    // textSnippets
    async loadInfoTextSnippet({ commit, rootGetters }) {
      const accessToken = rootGetters['auth/accessToken']

      const textSnippet = await textSnippetService.find('administration', { accessToken })

      commit('setInfoTextSnippet', { textSnippet })
    },
    async updateInfoTextSnippet({ commit, rootGetters }, { textSnippet }) {
      const accessToken = rootGetters['auth/accessToken']

      await textSnippetService.update('administration', textSnippet, { accessToken })
    },



    // attachment
    async createAttachment({ rootGetters }, { data }) {
      const accessToken = rootGetters['auth/accessToken']

      const result = await attachmentService.create(data, { accessToken })

      return result
    },

    // currentCustomer
    async deleteCurrentCustomer({ commit }) {
      window.localStorage.removeItem('currentCustomer')

      commit('deleteCurrentCustomer')
    },
    async loadCurrentCustomer({ commit }) {
      let customer = window.localStorage.getItem('currentCustomer')

      if (customer == null) {
        return
      }

      commit('setCurrentCustomer', { customer: JSON.parse(customer) })
    },
    async setCurrentCustomer({ commit }, { customer}) {
      let customerString = JSON.stringify(customer)
      window.localStorage.setItem('currentCustomer', customerString)

      commit('setCurrentCustomer', { customer })
    },

    // customer
    async createCustomer({ commit, rootGetters }, { customer }) {
      const accessToken = rootGetters['auth/accessToken']

      const result = await customerService.create(customer, { accessToken })

      customer.id = result.id

      commit('createCustomer', { customer })
    },
    async deleteCustomer({ commit, rootGetters }, { id }) {
      const accessToken = rootGetters['auth/accessToken']

      const result = await customerService.delete(id, { accessToken })

      if (result) {
        commit('deleteCustomer', { id })
      }
    },
    async inviteAllCustomers({ dispatch, getters }) {
      getters.customers.filter(c => c.state === 'open').forEach(c => {
        dispatch('inviteCustomer', { id: c.id })
      })
    },
    async inviteCustomer({ dispatch, rootGetters }, { id }) {
      const accessToken = rootGetters['auth/accessToken']

      const result = await customerService.invite(id, { accessToken })

      if (result) {
        dispatch('setCustomerState', {
          id,
          state: 'invited'
        })
      }
    },
    async loadCustomerByInvitationCode({ commit, getters, rootGetters }, { invitationCode }) {
      const accessToken = rootGetters['auth/accessToken']

      const customer = await customerService.findByInvitationCode(invitationCode, { accessToken })

      if (getters.customer(customer.id)) {
        commit('updateCustomer', { customer })
      } else {
        commit('createCustomer', { customer })
      }
    },
    async loadCustomerByShortCode({ commit, getters, rootGetters }, { shortCode }) {
      const accessToken = rootGetters['auth/accessToken']

      const customer = await customerService.findByShortCode(shortCode, { accessToken })

      if (getters.customer(customer.id)) {
        commit('updateCustomer', { customer })
      } else {
        commit('createCustomer', { customer })
      }

      return customer
    },
    async loadCustomers({ commit, getters, rootGetters }) {
      const accessToken = rootGetters['auth/accessToken']

      if (getters.customers.length !== 0) {
        return
      }


      const customers = await customerService.getAll({ accessToken })

      commit('setCustomers', { customers })
    },
    async setCustomerState({ dispatch, getters }, { id, state }) {
      const customer = { ...getters.customer(id) }

      if (customer == null) {
        return
      }

      customer.state = state

      dispatch('updateCustomer', { customer })
    },
    async resendInvitationCode({ rootGetters }, { email }) {
      const accessToken = rootGetters['auth/accessToken']

      await customerService.resendInvitationCode(email, { accessToken })
    },
    // Selbstbedienung (Registrierung ohne Login): der Server übernimmt nur die Registrierungsfelder (Allow-List).
    async registerCustomer({ commit }, { customer }) {
      const result = await customerService.updateByInvitationCode(customer.invitationCode, customer, {})

      if (result) {
        commit('updateCustomer', { customer })
      }

      return result
    },

    // Administration: autorisiert über die Kunden-Id (write:customers), alle Felder außer Codes.
    async updateCustomer({ commit, rootGetters }, { customer }) {
      const accessToken = rootGetters['auth/accessToken']

      const result = await customerService.update(customer.id, customer, { accessToken })

      if (!result) {
        return null
      }

      // Codes (Kunde und Gäste) vergibt nur der Server: nach dem Speichern neu laden, damit frisch
      // vergebene Gastcodes sichtbar werden. Zähler aus der Liste (orderCount usw.) bleiben erhalten.
      let saved = customer

      try {
        const fresh = await customerService.find(customer.id, { accessToken })

        if (fresh != null) {
          saved = { ...customer, ...fresh }
        }
      } catch (e) {
        // Anzeige bleibt dann beim gesendeten Stand; Codes erscheinen nach dem nächsten Laden
      }

      commit('updateCustomer', { customer: saved })

      return saved
    },

    // Anwesenheit einer Person: slot 0 = Kunde selbst, 1–10 = Gast (eigener Code, eigenes Kennzeichen)
    async setAttendingParticipant({ commit, rootGetters }, { customer, slot }) {
      const accessToken = rootGetters['auth/accessToken']
      const participant = participants(customer).find(p => p.slot === slot)

      if (participant == null || participant.invitationCode == null) {
        return
      }

      const result = await customerService.setAttending(participant.invitationCode, { accessToken })

      if (result) {
        const updated = { ...customer }

        if (slot === 0) {
          updated.wasAttending = true
        } else {
          updated[guestFieldNames(slot).wasAttending] = true
        }

        commit('updateCustomer', { customer: updated })
      }
    },

    async setAttendingCustomer({ dispatch }, { customer }) {
      await dispatch('setAttendingParticipant', { customer, slot: 0 })
    },

    // customEmailTemplate
    async createCustomEmailTemplate({ commit, rootGetters }, { customEmailTemplate }) {
      const accessToken = rootGetters['auth/accessToken']

      const result = await customEmailTemplateService.create(customEmailTemplate, { accessToken })

      customEmailTemplate.id = result.id

      commit('createCustomEmailTemplate', { customEmailTemplate })
    },
    async deleteCustomEmailTemplate({ commit, rootGetters }, { id }) {
      const accessToken = rootGetters['auth/accessToken']

      const result = await customEmailTemplateService.delete(id, { accessToken })

      if (result) {
        commit('deleteCustomEmailTemplate', { id })
      }
    },
   
    async loadCustomEmailTemplates({ commit, getters, rootGetters }) {
      const accessToken = rootGetters['auth/accessToken']

   

      const customEmailTemplates = await customEmailTemplateService.getAll({ accessToken })

      commit('setCustomEmailTemplates', { customEmailTemplates })
    },
    
    async updateCustomEmailTemplate({ commit, rootGetters }, { customEmailTemplate }) {
      const accessToken = rootGetters['auth/accessToken']

      const result = await customEmailTemplateService.update(customEmailTemplate.id, customEmailTemplate, { accessToken })

      if (result) {
        commit('updateCustomEmailTemplate', { customEmailTemplate })
      }
    },
    async duplicateCustomEmailTemplate({ commit, rootGetters }, { id, name, kind }) {
      const accessToken = rootGetters['auth/accessToken']

      const customEmailTemplate = await customEmailTemplateService.duplicate(id, { name, kind }, { accessToken })

      commit('createCustomEmailTemplate', { customEmailTemplate })

      return customEmailTemplate
    },
    async convertCustomEmailTemplate({ commit, rootGetters }, { id }) {
      const accessToken = rootGetters['auth/accessToken']

      const customEmailTemplate = await customEmailTemplateService.convert(id, { accessToken })

      commit('updateCustomEmailTemplate', { customEmailTemplate })

      return customEmailTemplate
    },
    async renderCustomEmailTemplate({ rootGetters }, { mjml, audience, sampleId }) {
      const accessToken = rootGetters['auth/accessToken']

      return await customEmailTemplateService.render({ mjml, audience, sampleId }, { accessToken })
    },
    async sendTestCustomEmailTemplate({ rootGetters }, { id, email, sampleId }) {
      const accessToken = rootGetters['auth/accessToken']

      return await customEmailTemplateService.sendTest(id, { email, sampleId }, { accessToken })
    },
    async loadEmailDesignerData({ commit, state, rootGetters }) {
      if (state.emailDesigner.loaded) {
        return
      }

      const accessToken = rootGetters['auth/accessToken']

      const [ settings, placeholders, callToActionTargets ] = await Promise.all([
        customEmailTemplateService.getSettings({ accessToken }),
        customEmailTemplateService.getPlaceholders({ accessToken }),
        customEmailTemplateService.getCallToActionTargets({ accessToken })
      ])

      commit('setEmailDesignerData', { settings, placeholders, callToActionTargets })
    },
    async loadEmailAssets({ commit, rootGetters }) {
      const accessToken = rootGetters['auth/accessToken']

      const assets = await emailAssetService.getAll({ accessToken })

      commit('setEmailAssets', { assets })
    },
    // emailJob (Outbox)
    async previewEmailJob({ rootGetters }, request) {
      const accessToken = rootGetters['auth/accessToken']

      return await emailJobService.preview(request, { accessToken })
    },
    async createEmailJob({ commit, rootGetters }, request) {
      const accessToken = rootGetters['auth/accessToken']

      const job = await emailJobService.create(request, { accessToken })

      commit('upsertEmailJob', { job })

      return job
    },
    async loadEmailJobs({ commit, rootGetters }, { limit } = {}) {
      const accessToken = rootGetters['auth/accessToken']

      const jobs = await emailJobService.getAll(limit, { accessToken })

      commit('setEmailJobs', { jobs })
    },
    async loadEmailJob({ commit, rootGetters }, { id }) {
      const accessToken = rootGetters['auth/accessToken']

      const job = await emailJobService.find(id, { accessToken })

      commit('upsertEmailJob', { job })

      return job
    },
    async loadEmailJobDeliveries({ rootGetters }, { id, status }) {
      const accessToken = rootGetters['auth/accessToken']

      return await emailJobService.getDeliveries(id, status, { accessToken })
    },
    async loadEmailDeliveriesForRecipient({ rootGetters }, { recipientId }) {
      const accessToken = rootGetters['auth/accessToken']

      return await emailJobService.getDeliveriesForRecipient(recipientId, { accessToken })
    },
    async retryEmailJob({ commit, rootGetters }, { id }) {
      const accessToken = rootGetters['auth/accessToken']

      const job = await emailJobService.retryFailed(id, { accessToken })

      commit('upsertEmailJob', { job })

      return job
    },
    async cancelEmailJob({ commit, rootGetters }, { id }) {
      const accessToken = rootGetters['auth/accessToken']

      const job = await emailJobService.cancel(id, { accessToken })

      commit('upsertEmailJob', { job })

      return job
    },
    async importDefaultEmailAssets({ dispatch, rootGetters }) {
      const accessToken = rootGetters['auth/accessToken']

      const result = await emailAssetService.importDefaults({ accessToken })

      await dispatch('loadEmailAssets')

      return result
    },
    async deleteEmailAsset({ commit, rootGetters }, { id }) {
      const accessToken = rootGetters['auth/accessToken']

      const result = await emailAssetService.delete(id, { accessToken })

      if (result) {
        commit('deleteEmailAsset', { id })
      }
    },


    // order
    async createOrderForMe({ commit, getters, rootGetters }, { order }) {
      const accessToken = rootGetters['auth/accessToken']
      const invitationCode = getters.currentCustomerInvitationCode

      const result = await orderService.createForMe(order, { accessToken, invitationCode })

      commit('createOrder', { order })
    },
    async createOrderForShortCode({ commit, getters, rootGetters }, { order, invitationCode, shortCode }) {
      const accessToken = rootGetters['auth/accessToken']

      const result = await orderService.createForShortCode(shortCode, order, { invitationCode })

      commit('createOrder', { order })
    },
    async createOrdersForMe({ commit, dispatch }, { orders }) {
      for (const o of orders) {
        await dispatch('createOrderForMe', { order: o })
      }
    },
    async createOrdersForShortCode({ commit, dispatch }, { orders, invitationCode, shortCode }) {
      for (const o of orders) {
        await dispatch('createOrderForShortCode', { order: o, invitationCode, shortCode })
      }

      /*orders.forEach(o => {
        dispatch('createOrderForShortCode', { order: o, invitationCode, shortCode })
      })*/
    },
    // TODO: delete
    async exportOrders({ getters, rootGetters }) {
      const accessToken = rootGetters['auth/accessToken']

      const orders = await orderService.exportAll({ accessToken })

      return orders
    },
    async exportOrdersForCustomer({ getters, rootGetters }, { customerId }) {
      const accessToken = rootGetters['auth/accessToken']

      const orders = await orderService.exportAllForCustomer(customerId, { accessToken })

      return orders
    },
    async exportOrdersForSupplier({ getters, rootGetters }, { supplierId }) {
      const accessToken = rootGetters['auth/accessToken']

      const orders = await orderService.exportAllForSupplier(supplierId, { accessToken })

      return orders
    },
    async exportOrdersForSupplierInvitation({ getters, rootGetters }, { invitationCode }) {

      const orders = await orderService.exportAllForSupplierInvitation({ invitationCode })

      return orders
    },
    async exportOrdersForMe({ getters, rootGetters }) {
      const accessToken = rootGetters['auth/accessToken']
      const invitationCode = getters.currentCustomerInvitationCode

      const orders = await orderService.exportAllForMe({ accessToken, invitationCode })

      return orders
    },
    async loadOrdersForCustomer({ commit, rootGetters }, { id }) {
      const accessToken = rootGetters['auth/accessToken']

      const orders = await orderService.getAllForCustomer(id, { accessToken })

      commit('setOrders', { orders })
    },
    async loadOrdersForMe({ commit, getters, rootGetters }) {
      const accessToken = rootGetters['auth/accessToken']
      const invitationCode = getters.currentCustomerInvitationCode

      const orders = await orderService.getAllForMe({ accessToken, invitationCode })

      commit('setOrders', { orders })
    },
    async loadOrdersForSupplierInvitation({ commit, getters, rootGetters }, { invitationCode }) {
      /*const accessToken = rootGetters['auth/accessToken']
      const invitationCode = getters.currentCustomerInvitationCode*/

      const orders = await orderService.getAllForSupplierInvitation({ invitationCode })

      commit('setOrders', { orders })
    },
    // TODO: update

    // externalDatabase (Sagasser SQL-Server, Quelle der SQL-Importe)
    async loadExternalDatabaseStatus({ rootGetters }) {
      const accessToken = rootGetters['auth/accessToken']

      return await externalDatabaseService.getStatus({ accessToken })
    },

    // csv import
    async importCustomersCsv({ commit, rootGetters }, { file, dryRun }) {
      const accessToken = rootGetters['auth/accessToken']

      const result = await csvImportService.importCustomers(file, { accessToken, dryRun })

      // Gecachte Kundenliste nach erfolgreichem Import erneuern (loadCustomers lädt nur, wenn die Liste leer ist).
      if (!dryRun && result.errors.length === 0) {
        const customers = await customerService.getAll({ accessToken })

        commit('setCustomers', { customers })
      }

      return result
    },
    async importSuppliersCsv({ dispatch, rootGetters }, { file, dryRun }) {
      const accessToken = rootGetters['auth/accessToken']

      const result = await csvImportService.importSuppliers(file, { accessToken, dryRun })

      if (!dryRun && result.errors.length === 0) {
        await dispatch('loadSuppliers')
      }

      return result
    },

    // quantity
    async importQuantities({ commit, rootGetters }) {
      const accessToken = rootGetters['auth/accessToken']

      await quantityService.import({ accessToken })
    },
    async clearQuantities({ commit, rootGetters }) {
      const accessToken = rootGetters['auth/accessToken']

      await quantityService.clear({ accessToken })
    },

    // price
    async importPrices({ commit, rootGetters }, { supplierId }) {
      const accessToken = rootGetters['auth/accessToken']

      await priceService.import(supplierId, { accessToken })
    },
    async clearPrices({ commit, rootGetters }) {
      const accessToken = rootGetters['auth/accessToken']

      await priceService.clear({ accessToken })
    },
    async loadPricesForCustomer({ commit, rootGetters }, { id }) {
      const accessToken = rootGetters['auth/accessToken']

      const prices = await priceService.getAllForCustomer(id, { accessToken })

      commit('setPrices', { prices })
    },
    async loadPricesForMe({ commit, getters, rootGetters, state }) {
      const accessToken = rootGetters['auth/accessToken']
      const invitationCode = getters.currentCustomerInvitationCode

      if (state.prices.length != 0) {
        return
      }

      const prices = await priceService.getAllForMe({ accessToken, invitationCode })

      commit('setPrices', { prices })
    },
    async loadPricesForShortCode({ commit, getters, rootGetters, state }, { shortCode, invitationCode }) {
      const prices = await priceService.getAllForShortCode(shortCode, { invitationCode })

      commit('setPrices', { prices })
    },

    // product
    // TODO: create
    // TODO: delete
    async importProducts({ commit, rootGetters }) {
      const accessToken = rootGetters['auth/accessToken']

      await productService.import({ accessToken })
    },
    async loadProductsForMe({ commit, getters, rootGetters }) {
      const accessToken = rootGetters['auth/accessToken']
      const invitationCode = getters.currentCustomerInvitationCode

      const products = await productService.getAllForMe({ accessToken, invitationCode })

      commit('setProducts', { products })
    },
    async loadProductsForMeNew({ commit, getters, rootGetters, state }) {
      const accessToken = rootGetters['auth/accessToken']
      const invitationCode = getters.currentCustomerInvitationCode

      if (state.suppiersProductNewInCache != null) {
        commit('setProducts', { products: state.suppiersProductNewInCache })

        return
      }

      const products = await productService.getAllForMeNew({ accessToken, invitationCode })

      state.suppiersProductNewInCache = products

      commit('setProducts', { products })
    },

    /**
     * Artikel eines Lieferanten für die Design-Vorschau. Mit Lieferanten-Einladungscode (Lieferantenseite) oder
     * mit Admin-Token (Administration). Gibt die Liste zurück, ohne den globalen Produktzustand zu verändern;
     * kundenspezifische Felder werden mit neutralen Werten belegt, damit die Produktliste sie darstellen kann.
     */
    async loadProductsForDesignPreview({ rootGetters }, { supplierId, invitationCode }) {
      const accessToken = rootGetters['auth/accessToken']

      let products = []

      try {
        products = invitationCode != null
          ? await productService.getAllForMeAsSupplier({ invitationCode })
          : await productService.getAllForSupplier(supplierId, { accessToken })
      } catch {
        products = []
      }

      return (products || []).map(p => ({
        ...p,
        discounts: [],
        orders: [],
        price: null,
        quantity: [ 0, 0 ],
        orderQuantity: [ 0, 0 ]
      }))
    },

    // infoDocuments – Vorabinformationen (PDF) der Startseite; Lesen anonym, Verwalten mit Admin-Token
    async loadInfoDocuments({ commit }) {
      const infoDocuments = await infoDocumentService.getPublished()

      commit('setInfoDocuments', { infoDocuments })
    },
    async loadAllInfoDocuments({ commit, rootGetters }) {
      const accessToken = rootGetters['auth/accessToken']

      const infoDocuments = await infoDocumentService.getAll({ accessToken })

      commit('setInfoDocuments', { infoDocuments })
    },
    async createInfoDocument({ commit, rootGetters }, { title, description, file }) {
      const accessToken = rootGetters['auth/accessToken']

      const infoDocument = await infoDocumentService.create({ title, description, file }, { accessToken })

      commit('addInfoDocument', { infoDocument })

      return infoDocument
    },
    async updateInfoDocument({ commit, rootGetters }, { id, title, description, isPublished }) {
      const accessToken = rootGetters['auth/accessToken']

      const infoDocument = await infoDocumentService.update(id, { title, description, isPublished }, { accessToken })

      commit('replaceInfoDocument', { infoDocument })

      return infoDocument
    },
    async replaceInfoDocumentFile({ commit, rootGetters }, { id, file }) {
      const accessToken = rootGetters['auth/accessToken']

      const infoDocument = await infoDocumentService.replaceFile(id, file, { accessToken })

      commit('replaceInfoDocument', { infoDocument })

      return infoDocument
    },
    async reorderInfoDocuments({ commit, rootGetters }, { ids }) {
      const accessToken = rootGetters['auth/accessToken']

      const infoDocuments = await infoDocumentService.reorder(ids, { accessToken })

      commit('setInfoDocuments', { infoDocuments })
    },
    async deleteInfoDocument({ commit, rootGetters }, { id }) {
      const accessToken = rootGetters['auth/accessToken']

      await infoDocumentService.delete(id, { accessToken })

      commit('removeInfoDocument', { id })
    },

    // productImages – Lieferant (Einladungscode) oder Administrator (Access-Token)
    async uploadProductImage({ rootGetters }, { productId, blob, invitationCode }) {
      const accessToken = rootGetters['auth/accessToken']

      return await productImageService.upload(productId, blob, { accessToken, invitationCode })
    },
    async deleteProductImage({ rootGetters }, { productId, invitationCode }) {
      const accessToken = rootGetters['auth/accessToken']

      return await productImageService.delete(productId, { accessToken, invitationCode })
    },
    async importLegacyProductImages({ rootGetters }) {
      const accessToken = rootGetters['auth/accessToken']

      return await productImageService.importLegacy({ accessToken })
    },
    async loadOrphanedProductImages({ rootGetters }) {
      const accessToken = rootGetters['auth/accessToken']

      return await productImageService.getOrphaned({ accessToken })
    },
    async deleteOrphanedProductImages({ rootGetters }) {
      const accessToken = rootGetters['auth/accessToken']

      return await productImageService.deleteOrphaned({ accessToken })
    },

    async loadProductsForShortCodeForSupplier({ commit, getters, rootGetters, state }, { supplierId, invitationCode, shortCode }) {
      const products = await productService.getAllForShortCodeForSupplier(shortCode, supplierId, { invitationCode })

      commit('setProducts', { products })
    },

    async loadProductsForMeForSupplier({ commit, getters, rootGetters, state }, { supplierId }) {
      const accessToken = rootGetters['auth/accessToken']
      const invitationCode = getters.currentCustomerInvitationCode

      /*let existingList = state.suppliersProductsInCache.find(s => s.id === supplierId)

      if (existingList != null) {
        commit('setProducts', { products: existingList.products })

        return
      }*/

      const products = await productService.getAllForMeForSupplier(supplierId, { accessToken, invitationCode })

      /*state.suppliersProductsInCache.push({
        id: supplierId,
        products
      })*/

      commit('setProducts', { products })
    },
    // TODO: update

    // supplier
    async createSupplier({ commit, rootGetters }, { supplier }) {
      const accessToken = rootGetters['auth/accessToken']

      const result = await supplierService.create(supplier, { accessToken })

      supplier.id = result.id

      commit('createSupplier', { supplier })
    },
    async deleteSupplier({ commit, rootGetters }, { id }) {
      const accessToken = rootGetters['auth/accessToken']

      const result = await supplierService.delete(id, { accessToken })

      if (result) {
        commit('deleteSupplier', { id })
      }
    },
    async inviteAllSuppliers({ dispatch, getters }) {
      getters.suppliers.filter(s => s.state === 'open').forEach(s => {
        dispatch('inviteSupplier', { id: s.id })
      })
    },
    async inviteSupplier({ dispatch, rootGetters }, { id }) {
      const accessToken = rootGetters['auth/accessToken']

      const result = await supplierService.invite(id, { accessToken })

      if (result) {
        dispatch('setSupplierState', {
          id,
          state: 'invited'
        })
      }
    },
    async loadSupplierByInvitationCode({ commit, getters, rootGetters }, { invitationCode }) {
      const accessToken = rootGetters['auth/accessToken']

      const supplier = await supplierService.findByInvitationCode(invitationCode, { accessToken })

      if (getters.supplier(supplier.id)) {
        commit('updateSupplier', { supplier })
      } else {
        commit('createSupplier', { supplier })
      }
    },
    async loadSuppliers({ commit, rootGetters }) {
      const accessToken = rootGetters['auth/accessToken']

      const suppliers = await supplierService.getAll({ accessToken })

      commit('setSuppliers', { suppliers })
    },
    async loadSuppliersForMe({ commit, getters, rootGetters }) {
      const accessToken = rootGetters['auth/accessToken']
      const invitationCode = getters.currentCustomerInvitationCode

      const suppliers = await supplierService.getAllForMe({ accessToken, invitationCode })

      commit('setSuppliers', { suppliers })
    },
    async setSupplierState({ dispatch, getters }, { id, state }) {
      const supplier = { ...getters.supplier(id) }

      if (supplier == null) {
        return
      }

      supplier.state = state

      dispatch('updateSupplier', { supplier })
    },
    async updateSupplier({ commit, rootGetters }, { supplier }) {
      const accessToken = rootGetters['auth/accessToken']

      const result = await supplierService.updateByInvitationCode(supplier.invitationCode, supplier, { accessToken })

      if (result) {
        commit('updateSupplier', { supplier })
      }
    }
  },
  getters: {
    evaluation: state => state.evaluation,
    infoTextSnippet: state => state.infoTextSnippet,
    infoDocuments: state => state.infoDocuments,
    publishedInfoDocuments: state => state.infoDocuments.filter(d => d.isPublished),
    isLive: (state, getters, rootState, rootGetters) => {
      if (rootGetters['auth/hasRole']('Administrator')) {
        return true
      }

      const now = new Date()

      return now >= Date.parse(state.event.startDate) && now <= Date.parse(state.event.endDate)

    },
    company: state => state.company,
    currentCustomer: state => state.currentCustomer,
    currentCustomerInvitationCode: state => {
      if (state.currentCustomer == null) {
        return null
      }

      return state.currentCustomer.invitationCode
    },
    customEmailTemplates: state => state.customEmailTemplates,
    customEmailTemplate: state => id => state.customEmailTemplates.find(t => t.id === id),
    emailAssets: state => state.emailAssets,
    emailJob: state => id => state.emailJobs.find(j => j.id === id),
    emailJobs: state => state.emailJobs,
    emailAssetUploadUrl: () => emailAssetService.uploadUrl,
    emailDesigner: state => state.emailDesigner,
    customer: state => id => state.customers.find(c => c.id === id),
    customerByInvitationCode: state => invitationCode => state.customers.find(c => c.invitationCode === invitationCode),
    //customerId: state => state.customerId, // TODO: Change
    customerOrderType: state => state.customerOrderType, // TODO: Change
    customers: state => state.customers,
    event: state => state.event,
    orders: state => state.orders,
    priceForProduct: state => productId => {
      if (state.currentCustomer == null) {
        return 0
      }

      const price = state.prices.find(p => p.productId === productId && p.customerId === state.currentCustomer.id)

      if (price == null) {
        return 0
      }

      return price.value
    },
    priceForProductAndCustomer: state => (productId, customerId) => {
      const price = state.prices.find(p => p.productId === productId && p.customerId === customerId)

      if (price == null) {
        return 0
      }

      return price.value
    },
    product: state => id => state.products.find(p => p.id === id),
    products: state => state.products,
    productsForSupplier: state => supplierId => state.products.filter(p => p.supplierId === supplierId),
    productTitle: state => id => {
      const product = state.products.find(p => p.id === id)

      if (product == null) {
        return null
      }

      return product.title
    },
    supplier: state => id => state.suppliers.find(s => s.id === id),
    supplierByInvitationCode: state => invitationCode => state.suppliers.find(s => s.invitationCode === invitationCode),
    suppliers: state => state.suppliers,
    suppliersForOrderType: state => orderType => state.suppliers, // TODO: Implement functionality
    supplierCompanyName: state => id => {
      const supplier = state.suppliers.find(s => s.id === id)

      if (supplier == null) {
        return null
      }

      return supplier.companyName
    }
  },
  modules: {
    auth: createAuthStoreModule(auth0Settings)
  },
  mutations: {
    // evaluation
    setEvaluation(state, { evaluation }) {
      state.evaluation = evaluation
    },

    // currentCustomer
    deleteCurrentCustomer(state) {
      state.currentCustomer = null
    },
    setCurrentCustomer(state, { customer }) {
      state.products = []
      state.prices = []
      state.currentCustomer = customer
    },

    // customer
    createCustomer(state, { customer }) {
      state.customers.push(customer)
    },
    deleteCustomer(state, { id }) {
      const index = state.customers.findIndex(c => c.id === id)

      if (index !== -1) {
        state.customers.splice(index, 1)
      }
    },
    setCustomers(state, { customers }) {
      state.customers = customers
    },
    updateCustomer(state, { customer }) {
      const index = state.customers.findIndex(c => c.id === customer.id)

      Vue.set(state.customers, index, customer)
    },

    // customEmailTemplate
    createCustomEmailTemplate(state, { customEmailTemplate }) {
      state.customEmailTemplates.push(customEmailTemplate)
    },
    deleteCustomEmailTemplate(state, { id }) {
      const index = state.customEmailTemplates.findIndex(c => c.id === id)

      if (index !== -1) {
        state.customEmailTemplates.splice(index, 1)
      }
    },
    setCustomEmailTemplates(state, { customEmailTemplates }) {
      state.customEmailTemplates = customEmailTemplates
    },
    updateCustomEmailTemplate(state, { customEmailTemplate }) {
      const index = state.customEmailTemplates.findIndex(c => c.id === customEmailTemplate.id)

      Vue.set(state.customEmailTemplates, index, customEmailTemplate)
    },
    setEmailDesignerData(state, { settings, placeholders, callToActionTargets }) {
      state.emailDesigner = { loaded: true, settings, placeholders, callToActionTargets }
    },
    setEmailJobs(state, { jobs }) {
      state.emailJobs = jobs
    },
    upsertEmailJob(state, { job }) {
      const index = state.emailJobs.findIndex(j => j.id === job.id)

      if (index === -1) {
        state.emailJobs.unshift(job)
      } else {
        Vue.set(state.emailJobs, index, job)
      }
    },
    setEmailAssets(state, { assets }) {
      state.emailAssets = assets
    },
    deleteEmailAsset(state, { id }) {
      const index = state.emailAssets.findIndex(a => a.id === id)

      if (index !== -1) {
        state.emailAssets.splice(index, 1)
      }
    },

    // order
    createOrder(state, { order }) {
      state.orders.push(order)
    },
    deleteOrder(state, { customerId, deliveryPeriod, productId }) {
      const index = state.suppliers.findIndex(s => s.customerId === customerId && s.deliveryPeriod === deliveryPeriod && s.productId === productId)

      if (index !== -1) {
        state.suppliers.splice(index, 1)
      }
    },
    setOrders(state, { orders }) {
      state.orders = orders
    },
    updateOrder(state, { order }) {
      const index = state.orders.findIndex(o => o.customerId === order.customerId && o.deliveryPeriod === order.deliveryPeriod && o.productId === order.productId)

      Vue.set(state.orders, index, order)
    },

    // price
    setPrices(state, { prices }) {
      state.prices = prices
    },

    // product
    // TODO: create
    // TODO: delete
    setProducts(state, { products }) {
      state.products = products
    },
    // TODO: update

    // supplier
    createSupplier(state, { supplier }) {
      state.suppliers.push(supplier)
    },
    deleteSupplier(state, { id }) {
      const index = state.suppliers.findIndex(s => s.id === id)

      if (index !== -1) {
        state.suppliers.splice(index, 1)
      }
    },
    setSuppliers(state, { suppliers }) {
      state.suppliers = suppliers
    },
    updateSupplier(state, { supplier }) {
      const index = state.suppliers.findIndex(s => s.id === supplier.id)

      Vue.set(state.suppliers, index, supplier)
    },

    // textSnippets
    // infoDocuments
    setInfoDocuments(state, { infoDocuments }) {
      state.infoDocuments = infoDocuments
    },
    addInfoDocument(state, { infoDocument }) {
      state.infoDocuments.push(infoDocument)
    },
    replaceInfoDocument(state, { infoDocument }) {
      const index = state.infoDocuments.findIndex(d => d.id === infoDocument.id)

      if (index === -1) {
        state.infoDocuments.push(infoDocument)
      } else {
        state.infoDocuments.splice(index, 1, infoDocument)
      }
    },
    removeInfoDocument(state, { id }) {
      state.infoDocuments = state.infoDocuments.filter(d => d.id !== id)
    },
    setInfoTextSnippet(state, { textSnippet }) {
      state.infoTextSnippet = textSnippet
    },

    deleteSupplierInCache(state, { id }) {
      console.log(id)
      const index = state.suppliersProductsInCache.findIndex(s => s.id === id)

      if (index !== -1) {
        state.suppliersProductsInCache.splice(index, 1)
      }
    },
  },
  state: {
    company: {
      title: 'SAGASSER Hausmesse 2027'
    },
    currentCustomer: null,
    customEmailTemplates: [],
    emailAssets: [],
    emailJobs: [],
    emailDesigner: {
      loaded: false,
      settings: null,
      placeholders: [],
      callToActionTargets: []
    },
    //currentCustomerInvitationCode: null, // TODO: Load
    customers: [],
    event: {
      title: 'Hausmesse 2027',
      // Platzhalter-Termine 2027, noch nicht final (siehe VORBEREITUNG-2027.md)
      startDate: '2027-02-22T10:00:00',
      endDate: '2027-02-28T23:00:00',
      siteStartDate: '2027-02-28T10:00',
      siteEndDate: '2027-02-28T16:00'
    },
    orders: [],
    prices: [],
    products: [],
    suppliers: [],
    suppliersProductsInCache: [],
    suppiersProductNewInCache: null,
    infoTextSnippet: null,
    infoDocuments: [],
    evaluation: null
  }
})